import { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { useGame } from '../state/store'

interface Toast {
  id: number
  text: string
  type: 'info' | 'success' | 'milestone'
}

let nextToastId = 0

// Global toast emitter — components can import and call this
let emitToastFn: ((text: string, type?: Toast['type']) => void) | null = null

export function emitToast(text: string, type: Toast['type'] = 'info') {
  emitToastFn?.(text, type)
}

export function ToastFeed() {
  const [toasts, setToasts] = useState<Toast[]>([])
  const lastCraft = useGame((s) => s.lastCraft)

  // Register emitter
  useEffect(() => {
    emitToastFn = (text, type = 'info') => {
      const id = nextToastId++
      setToasts((prev) => [...prev.slice(-4), { id, text, type }])
      setTimeout(() => setToasts((prev) => prev.filter((t) => t.id !== id)), 3000)
    }
    return () => { emitToastFn = null }
  }, [])

  // Toast on craft burst
  useEffect(() => {
    if (lastCraft?.status === 'burst' && lastCraft.isNew) {
      emitToast(`New word: ${lastCraft.word}`, 'success')
    }
  }, [lastCraft])

  return (
    <div className="fixed bottom-4 right-4 z-50 flex flex-col gap-2">
      <AnimatePresence>
        {toasts.map((t) => (
          <motion.div
            key={t.id}
            initial={{ opacity: 0, x: 50, scale: 0.95 }}
            animate={{ opacity: 1, x: 0, scale: 1 }}
            exit={{ opacity: 0, x: 50, scale: 0.95 }}
            transition={{ duration: 0.2 }}
            className={`rounded-lg border px-3 py-2 text-xs shadow-lg backdrop-blur ${
              t.type === 'success'
                ? 'border-ink-tealdim bg-ink-panel/90 text-ink-teal'
                : t.type === 'milestone'
                ? 'border-rarity-legend/40 bg-ink-panel/90 text-rarity-legend'
                : 'border-ink-border bg-ink-panel/90 text-ink-text'
            }`}
          >
            {t.text}
          </motion.div>
        ))}
      </AnimatePresence>
    </div>
  )
}
